// Раздел «Задачи»: зеркало задач Bitrix — что на ком, к какому сроку и что внутри.
//
// ЗАЧЕМ ЭТОТ ЭКРАН. «Задачи и проекты» — самая большая очередь ожидания CRM: 18 человек
// живут в задачах Bitrix каждый день. Зеркало вывезено 29.07.2026 (bxt_tasks, синк раз
// в час), ядро отвечает на /tasks/* (tasks_api.py), а экрана не было вовсе.
//
// СТАРЫЕ ДАННЫЕ — ИСТОРИЯ, И ПОКАЗЫВАЮТСЯ КАК ЕСТЬ. Правило владельца: ничего не
// исправляем — ни в зеркале, ни тем более в Bitrix. Странность на экране — оговорка
// рядом с данными, а не задача владельцу. Просроченное с 2025 года стоит просроченным.
//
// РАЗДЕЛ ТОЛЬКО НА ЧТЕНИЕ, И ЭКРАН ГОВОРИТ ЭТО ВСЛУХ. Закрыть задачу, передвинуть срок
// или написать комментарий отсюда нельзя: на задачах Bitrix висят бизнес-процессы,
// которые ставят работу живым людям, и запись из CRM их запустит. Включение записи —
// отдельный шаг и отдельное решение владельца.
//
// СРОК — ТРИ РАЗНЫХ СОСТОЯНИЯ, А НЕ ДВА. У четверти открытых задач срока нет вовсе, и
// это норма, а не недосмотр: Bitrix срока не требует. Состояние срока считает ядро
// одним выражением и присылает готовым (`due_state`) — экран его только называет
// словами. Считать здесь свою просрочку нельзя: два места сравнения дат разъедутся на
// границе суток, и «просрочено» вспыхнет там, где срока не было никогда.
//
// КОММЕНТАРИИ ДОЕЗЖАЮТ ВТОРЫМ, НОЧНЫМ ПРОХОДОМ СИНКА — и раздел обязан работать при
// нуле комментариев. «Комментариев нет» — это ТРИ разных ответа, ядро различает их
// водяным знаком синка (comments_coverage), а экран обязан произнести каждый своими
// словами: «синк не ходил», «до задачи не дошли», «эту задачу проход видел».
//
// ВЫРЕЗАННОЕ СИНКОМ ПОКАЗЫВАЕТСЯ ВЫРЕЗАННЫМ, А НЕ ПУСТЫМ. Маски персональных данных
// срезают текст ещё в синке, и рядом лежит причина (title_redacted и т.п.). Пустое
// название и вырезанное название — разные ответы, и экран их различает.
//
// ТРАНСПОРТ ЗДЕСЬ ЖЕ, А НЕ В src/api. Раздел пока один экран (как BpeTasks): когда
// включат запись, транспорт всё равно переедет в api/tasks.ts вместе с ручкой ответа.

import { useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'

import { CoreUnsupported, Unauthorized } from '../api/client'
import { FilterBar } from '../components/FilterBar'
import { QueryState } from '../components/QueryState'
import { fmtDate, fmtText, pluralN } from '../lib/format'

// ───────────────────────────── транспорт ─────────────────────────────

const БАЗА = '/api/tasks'

/** Тот же разбор ответа, что в api/client.ts и pages/BpeTasks.tsx. */
async function запрос<T>(путь: string): Promise<T> {
  const res = await fetch(БАЗА + путь, { credentials: 'same-origin' })
  if (res.status === 401) throw new Unauthorized()
  const текст = await res.text()
  const данные: unknown = текст ? JSON.parse(текст) : {}
  if (!res.ok) {
    const rec = (данные || {}) as Record<string, unknown>
    // 400 от ядра — отказ по существу фильтра. Пустая таблица читалась бы как «задач
    // нет», а у людей, которые ждут этот раздел, задачи есть.
    if (res.status === 400) throw new CoreUnsupported(String(rec.why ?? rec.error ?? ''))
    const msg = (rec.why as string) || (rec.error as string) || `HTTP ${res.status}`
    throw new Error(msg)
  }
  return данные as T
}

function строкаЗапроса(ф: Record<string, unknown>): string {
  const p = new URLSearchParams()
  for (const [k, v] of Object.entries(ф)) {
    if (v === undefined || v === null || v === '' || v === false) continue
    p.set(k, v === true ? '1' : String(v))
  }
  const s = p.toString()
  return s ? '?' + s : ''
}

const ОБЩЕЕ = { staleTime: 60_000, retry: 1 } as const

// ─────────── формы ответов ядра (см. tasks_api.py) ───────────

/** Состояние срока. Считает ядро; экран только называет его словами. */
type ВидСрока = 'закрыто' | 'без_срока' | 'просрочено' | 'сегодня' | 'в_срок'

interface ЗадачаСтрока {
  bitrix_id: number
  title: string | null
  /** Почему названия нет. NULL — перенесено целиком; строка — причина маски синка. */
  title_redacted: string | null
  status: number | null
  priority: number | null
  mark: string | null
  created_by: number | null
  created_by_name: string | null
  responsible_id: number | null
  responsible_name: string | null
  responsible_active: boolean | null
  created_date: string | null
  changed_date: string | null
  closed_date: string | null
  activity_date: string | null
  deadline: string | null
  group_id: number | null
  group_name: string | null
  crm_deal_id: number | null
  deal_title: string | null
  deal_gone: boolean
  has_files: boolean
  files_count: number
  comments_count: number | null
  due_in_days: number | null
  age_days: number | null
  due_state: ВидСрока
}

interface Список<T> {
  rows: T[]
  total: number
  next: number | null
  limit: number
  start: number
  /** Сколько задач в зеркале вообще. Пустую страницу без него прочитать нельзя. */
  mirror_total: number
  why_empty?: string | null
  sort_note?: string | null
}

interface Человек {
  id: string | number
  name: string | null
  active: boolean | null
  open?: number
  tasks?: number
}

interface Проект {
  id: number | null
  name: string | null
  tasks: number
  open: number
}

interface СтатусСтрока {
  status: number | null
  status_name?: string | null
  n?: number
  tasks?: number
  no_due?: number
  overdue?: number
}

interface Справочники {
  source: string
  counts: Record<string, number | string | null>
  statuses: СтатусСтрока[]
  people: Человек[]
  authors: Человек[]
  groups: Проект[]
  comments: {
    in_mirror: number
    declared: number
    watermark: number | null
    started: boolean
    synced_at: string | null
  }
  caveats: string[]
  status_names: Record<string, string>
  priority_names: Record<string, string>
  due_state_names: Record<string, string>
  write_enabled: boolean
  write_why: string
}

interface Исполнитель {
  id: string
  name: string
  active: boolean | null
}

interface Комментарий {
  bitrix_id: number
  author: string
  author_active: boolean | null
  post_date: string | null
  message: string | null
  message_redacted: string | null
  has_files: boolean
  files_count: number
  files: { name?: string; size?: number }[] | null
}

interface Карточка {
  row: ЗадачаСтрока & {
    description: string | null
    description_redacted: string | null
    uf_notes: string | null
    uf_notes_redacted: string | null
    status_changed_date: string | null
    date_start: string | null
    accomplices_info: Исполнитель[]
    auditors_info: Исполнитель[]
    synced_at: string | null
  }
  comments: Комментарий[]
  comments_total: number
  comments_declared: number
  comments_coverage: {
    state: 'not_started' | 'not_reached' | 'covered'
    why: string | null
    watermark: number | null
    in_mirror_total: number
    synced_at: string | null
  }
  write_enabled: boolean
  write_why: string
}

interface СтрокаСвода {
  responsible_id: number | null
  name: string | null
  active: boolean | null
  tasks: number
  pending: number
  in_progress: number
  control: number
  deferred: number
  no_due: number
  overdue: number
  due_today: number
  high: number
  oldest_days: number | null
}

interface Свод {
  rows: СтрокаСвода[]
  total: number
  totals: { people: number; tasks: number; overdue: number; no_due: number }
  sum_note: string
}

interface Сводка {
  by_status: СтатусСтрока[]
  by_group: { group_id: number | null; group_name: string | null; tasks: number
    open: number; overdue: number }[]
  by_month: { month: string; created: number; closed: number; still_open: number }[]
  totals: { tasks: number; open: number; overdue: number }
  status_names: Record<string, string>
  group_note: string
}

// ─────────── хуки ───────────

const useСправочники = () =>
  useQuery({ queryKey: ['tasks', 'refs'], queryFn: () => запрос<Справочники>('/refs'), ...ОБЩЕЕ })

const useСписок = (ф: Record<string, unknown>) =>
  useQuery({
    queryKey: ['tasks', 'list', ф],
    queryFn: () => запрос<Список<ЗадачаСтрока>>('/list' + строкаЗапроса(ф)),
    ...ОБЩЕЕ,
  })

const useКарточка = (id: number | null) =>
  useQuery({
    queryKey: ['tasks', 'item', id],
    queryFn: () => запрос<Карточка>(`/item?id=${id ?? 0}`),
    enabled: !!id,
    ...ОБЩЕЕ,
  })

const useСвод = () =>
  useQuery({ queryKey: ['tasks', 'by-user'], queryFn: () => запрос<Свод>('/by-user'), ...ОБЩЕЕ })

const useСводка = () =>
  useQuery({ queryKey: ['tasks', 'summary'], queryFn: () => запрос<Сводка>('/summary'), ...ОБЩЕЕ })

// ───────────────────────────── подписи ─────────────────────────────

const СТРАНИЦА = 50

/** Ряд элементов в строку — та же раскладка и та же причина, что в BpeTasks: это
 * кнопки-вкладки, а не поля, сужающие список, — класс `.filters` им не положен. */
const РЯД = {
  display: 'flex', gap: 'var(--s3)', flexWrap: 'wrap', alignItems: 'flex-end',
} as const

/**
 * Срок словами и цветом. «Срока нет» — отдельное, спокойное состояние: оно НЕ красное
 * и никогда не называется просрочкой. Значение считает ядро — здесь только подпись.
 */
function видСрока(р: Pick<ЗадачаСтрока, 'due_state' | 'deadline' | 'closed_date'>):
{ ярлык: string; класс: string; смысл: string } {
  switch (р.due_state) {
    case 'просрочено':
      return { ярлык: 'срок прошёл', класс: 'badge lose', смысл: `срок был ${fmtDate(р.deadline, true)}` }
    case 'сегодня':
      return { ярлык: 'срок сегодня', класс: 'badge work', смысл: `до ${fmtDate(р.deadline, true)}` }
    case 'в_срок':
      return { ярлык: 'в срок', класс: 'badge won', смысл: `срок ${fmtDate(р.deadline, true)}` }
    case 'закрыто':
      return { ярлык: 'закрыто', класс: 'badge', смысл: `закрыта ${fmtDate(р.closed_date, true)}` }
    default:
      return {
        ярлык: 'срока нет',
        класс: 'badge',
        смысл: 'срок этой задаче не назначали — это не просрочка',
      }
  }
}

/**
 * Название задачи, различающее пустоту и маску. Синк вырезает свободный текст, где
 * маски нашли персональные данные, и кладёт рядом причину — её и показываем: человек,
 * который знает, что задача в Bitrix называется иначе, должен видеть, что это решение,
 * а не потеря.
 */
const названиеЗадачи = (р: Pick<ЗадачаСтрока, 'title' | 'title_redacted' | 'bitrix_id'>): string =>
  (р.title_redacted
    ? `[название вырезано синком: ${р.title_redacted}] — задача №${р.bitrix_id}`
    : fmtText(р.title))

const неРаботает = (активен: boolean | null) => активен === false

// ───────────────────────────── экран ─────────────────────────────

type Вкладка = 'list' | 'people' | 'summary'

const ВКЛАДКИ: { код: Вкладка; имя: string }[] = [
  { код: 'list', имя: 'Список' },
  { код: 'people', имя: 'На ком висит' },
  { код: 'summary', имя: 'Сводка' },
]

export default function Tasks() {
  const [вкладка, setВкладка] = useState<Вкладка>('list')
  const спр = useСправочники()
  const с = спр.data

  return (
    <>
      <div className="page-head">
        <h1>Задачи</h1>
        <span className="spacer" />
        <span className="hint">
          {с ? `${pluralN(Number(с.counts.open ?? 0), 'открытая задача', 'открытые задачи',
            'открытых задач')} · срок прошёл у ${Number(с.counts.overdue ?? 0)}` : ''}
        </span>
      </div>

      {/* Оговорки приходят от ядра вместе с данными и стоят ДО них: экран, который их
          спрячет, соврёт молча — «нет срока» тут не то же самое, что «просрочено», а
          «комментариев нет» не то же самое, что «ещё не доехали». */}
      {с && с.caveats.length > 0 && (
        <div className="notice" style={{ marginBottom: 16 }}>
          <b>Как это читать.</b>
          <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
            {с.caveats.map((т) => <li key={т}>{т}</li>)}
          </ul>
        </div>
      )}

      <div className="card" style={{ marginBottom: 16 }}>
        <div style={РЯД}>
          {ВКЛАДКИ.map((в) => (
            <button key={в.код} type="button" onClick={() => setВкладка(в.код)}
                    disabled={вкладка === в.код}>
              {в.имя}
            </button>
          ))}
        </div>
        <div className="hint" style={{ marginTop: 8 }}>
          Раздел читает зеркало Bitrix и ничего в нём не меняет: закрыть задачу или
          написать комментарий отсюда нельзя — на задачах висят бизнес-процессы, и
          запись из CRM их запустила бы.
        </div>
      </div>

      {вкладка === 'list' && <СписокЗадач спр={с} />}
      {вкладка === 'people' && <НаКомВисит />}
      {вкладка === 'summary' && <СводкаЗадач />}
    </>
  )
}

// ───────────────────────── вкладка «Список» ─────────────────────────

function СписокЗадач({ спр }: { спр?: Справочники }) {
  const [who, setWho] = useState('')
  const [by, setBy] = useState('')
  const [status, setStatus] = useState('')
  const [due, setDue] = useState('')
  const [state, setState] = useState('open')
  const [group, setGroup] = useState('')
  const [q, setQ] = useState('')
  const [start, setStart] = useState(0)
  const [открыто, setОткрыто] = useState<number | null>(null)

  const фильтры = useMemo(() => ({
    who: who || undefined,
    by: by || undefined,
    status: status || undefined,
    due: due || undefined,
    state,
    group: group || undefined,
    q: q.trim() || undefined,
    start,
    limit: СТРАНИЦА,
  }), [who, by, status, due, state, group, q, start])

  const зпр = useСписок(фильтры)
  const д = зпр.data
  const сСброса = <T,>(setter: (v: T) => void) => (v: T) => { setStart(0); setter(v) }

  return (
    <>
      <div className="card" style={{ marginBottom: 16 }}>
        <FilterBar id="tasks-list">
          <div className="f" style={{ minWidth: 220 }}>
            <label htmlFor="tskw">Ответственный</label>
            <select id="tskw" value={who} onChange={(e) => сСброса(setWho)(e.target.value)}
                    disabled={!спр}>
              <option value="">любой</option>
              {(спр?.people ?? []).map((ч) => (
                <option key={String(ч.id)} value={String(ч.id)}>
                  {fmtText(ч.name)} ({ч.open ?? 0}){неРаботает(ч.active) ? ' — не работает' : ''}
                </option>
              ))}
            </select>
          </div>
          <div className="f" style={{ minWidth: 200 }}>
            <label htmlFor="tskb">Постановщик</label>
            <select id="tskb" value={by} onChange={(e) => сСброса(setBy)(e.target.value)}
                    disabled={!спр}>
              <option value="">любой</option>
              {(спр?.authors ?? []).map((ч) => (
                <option key={String(ч.id)} value={String(ч.id)}>
                  {fmtText(ч.name)} ({ч.open ?? 0}){неРаботает(ч.active) ? ' — не работает' : ''}
                </option>
              ))}
            </select>
          </div>
          <div className="f">
            <label htmlFor="tsks">Статус</label>
            <select id="tsks" value={status} onChange={(e) => сСброса(setStatus)(e.target.value)}>
              <option value="">любой</option>
              {(спр?.statuses ?? []).map((с2) => (
                <option key={String(с2.status)} value={String(с2.status ?? '')}>
                  {с2.status_name ?? String(с2.status)} ({с2.n ?? 0})
                </option>
              ))}
            </select>
          </div>
          {/* Отбор по сроку. «Срока нет» — обычный пункт наравне с остальными, а не
              подвид просрочки: у четверти открытых задач срока нет вовсе. */}
          <div className="f">
            <label htmlFor="tskd">Срок</label>
            <select id="tskd" value={due} onChange={(e) => сСброса(setDue)(e.target.value)}>
              <option value="">любой</option>
              <option value="overdue">срок прошёл</option>
              <option value="today">срок сегодня</option>
              <option value="future">срок впереди</option>
              <option value="none">срока нет</option>
              <option value="has">срок назначен</option>
            </select>
          </div>
          <div className="f">
            <label htmlFor="tskst">Состояние</label>
            <select id="tskst" value={state} onChange={(e) => сСброса(setState)(e.target.value)}>
              <option value="open">открытые</option>
              <option value="closed">завершённые</option>
              <option value="all">все</option>
            </select>
          </div>
          <div className="f" style={{ minWidth: 180 }}>
            <label htmlFor="tskg">Проект</label>
            <select id="tskg" value={group} onChange={(e) => сСброса(setGroup)(e.target.value)}>
              <option value="">любой</option>
              {(спр?.groups ?? []).map((п) => (
                <option key={String(п.id)} value={String(п.id ?? '')}>
                  {fmtText(п.name)} ({п.open})
                </option>
              ))}
            </select>
          </div>
          <div className="f" style={{ flex: 2, minWidth: 200 }}>
            <label htmlFor="tskq">Название, проект или сделка</label>
            <input id="tskq" value={q} onChange={(e) => сСброса(setQ)(e.target.value)}
                   placeholder="например: акт сверки" />
          </div>
        </FilterBar>
      </div>

      <QueryState isLoading={зпр.isLoading} error={зпр.error}>
        <div className="card">
          <div className="table-wrap cards">
            <table>
              <thead>
                <tr>
                  <th>Задача</th>
                  <th className="nowrap">Статус</th>
                  <th>Ответственный</th>
                  <th>Постановщик</th>
                  <th className="nowrap">Срок</th>
                  <th className="num">Возраст</th>
                  <th>Сделка</th>
                  <th className="nowrap">Карточка</th>
                </tr>
              </thead>
              <tbody>
                {(д?.rows ?? []).map((р) => (
                  <СтрокаЗадачи key={р.bitrix_id} р={р} спр={спр}
                                открыть={() => setОткрыто(р.bitrix_id)} />
                ))}
                {д && д.rows.length === 0 && (
                  <tr>
                    <td colSpan={8} className="empty">
                      <div className="empty-title">
                        {д.mirror_total > 0
                          ? 'Под эти условия не попала ни одна задача'
                          : 'В зеркале пока нет ни одной задачи'}
                      </div>
                      <div className="empty-hint">{д.why_empty}</div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {д?.sort_note && <div className="hint" style={{ marginTop: 8 }}>{д.sort_note}</div>}

          {д && д.total > СТРАНИЦА && (
            <div className="pager">
              <button disabled={start === 0 || зпр.isFetching}
                      onClick={() => setStart(Math.max(0, start - СТРАНИЦА))}>
                ← назад
              </button>
              <span className="muted">
                {start + 1}–{Math.min(start + СТРАНИЦА, д.total)} из {д.total}
              </span>
              <button disabled={д.next === null || зпр.isFetching}
                      onClick={() => setStart(д.next!)}>вперёд →</button>
            </div>
          )}
        </div>
      </QueryState>

      {открыто !== null && (
        <КарточкаЗадачи id={открыто} спр={спр} закрыть={() => setОткрыто(null)} />
      )}
    </>
  )
}

function СтрокаЗадачи({ р, спр, открыть }: {
  р: ЗадачаСтрока
  спр?: Справочники
  открыть: () => void
}) {
  const срок = видСрока(р)
  return (
    <tr>
      <td data-label="Задача">
        {названиеЗадачи(р)}
        {р.group_name && (
          <div className="hint">{р.group_name}</div>
        )}
      </td>
      <td className="nowrap" data-label="Статус">
        {спр?.status_names[String(р.status)] ?? fmtText(р.status)}
      </td>
      <td data-label="Ответственный">
        {fmtText(р.responsible_name)}
        {неРаботает(р.responsible_active) && (
          <span className="badge lose" style={{ marginLeft: 6 }}
                title="учётка отключена, а задача осталась — в Bitrix такие висят молча">
            не работает
          </span>
        )}
      </td>
      <td data-label="Постановщик">{fmtText(р.created_by_name)}</td>
      {/* Три разных состояния срока, и «срока нет» среди них спокойное. */}
      <td className="nowrap" data-label="Срок">
        <span className={срок.класс} title={срок.смысл}>{срок.ярлык}</span>
        {р.deadline && (
          <div className="hint">{fmtDate(р.deadline)}</div>
        )}
      </td>
      <td className="num" data-label="Возраст">
        {р.age_days === null ? '—' : pluralN(р.age_days, 'день', 'дня', 'дней')}
      </td>
      <td data-label="Сделка">
        {р.crm_deal_id
          ? (р.deal_gone ? `сделка №${р.crm_deal_id} (в зеркале её нет)` : fmtText(р.deal_title))
          : '—'}
      </td>
      <td className="nowrap" data-label="Карточка">
        <button type="button" onClick={открыть}>Открыть</button>
      </td>
    </tr>
  )
}

// ───────────────────────── карточка задачи ─────────────────────────

function КарточкаЗадачи({ id, спр, закрыть }: {
  id: number
  спр?: Справочники
  закрыть: () => void
}) {
  const зпр = useКарточка(id)
  const к = зпр.data

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div className="page-head" style={{ marginBottom: 8 }}>
        <b>{к ? названиеЗадачи(к.row) : 'Задача'}</b>
        <span className="spacer" />
        <button type="button" onClick={закрыть}>Закрыть</button>
      </div>

      <QueryState isLoading={зпр.isLoading} error={зпр.error}>
        {к && (
          <>
            {/* Причина, по которой отсюда нельзя ничего поменять, стоит ДО содержимого:
                человек, который ищет кнопку «Завершить», должен сперва прочитать, почему
                её нет. */}
            <div className="hint" style={{ marginBottom: 8 }}>{к.write_why}</div>

            {к.row.description_redacted ? (
              <p className="hint">
                Описание вырезано синком: {к.row.description_redacted}. Это маска
                персональных данных, а не потеря — читать полный текст пока можно в Bitrix.
              </p>
            ) : (
              к.row.description && <p style={{ whiteSpace: 'pre-wrap' }}>{к.row.description}</p>
            )}

            <div className="table-wrap cards">
              <table>
                <thead>
                  <tr>
                    <th className="nowrap">Статус</th>
                    <th>Ответственный</th>
                    <th>Постановщик</th>
                    <th className="nowrap">Срок</th>
                    <th className="nowrap">Поставлена</th>
                    <th className="nowrap">Завершена</th>
                    <th>Проект</th>
                    <th>Сделка</th>
                    <th className="num">Файлы</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="nowrap" data-label="Статус">
                      {спр?.status_names[String(к.row.status)] ?? fmtText(к.row.status)}
                      {к.row.priority !== null && к.row.priority !== 1 && (
                        <div className="hint">
                          приоритет: {спр?.priority_names[String(к.row.priority)]
                            ?? fmtText(к.row.priority)}
                        </div>
                      )}
                    </td>
                    <td data-label="Ответственный">
                      {fmtText(к.row.responsible_name)}
                      {неРаботает(к.row.responsible_active) && (
                        <span className="badge lose" style={{ marginLeft: 6 }}>не работает</span>
                      )}
                    </td>
                    <td data-label="Постановщик">{fmtText(к.row.created_by_name)}</td>
                    <td className="nowrap" data-label="Срок">
                      <span className={видСрока(к.row).класс}>{видСрока(к.row).ярлык}</span>
                      {к.row.deadline && <div className="hint">{fmtDate(к.row.deadline, true)}</div>}
                    </td>
                    <td className="nowrap" data-label="Поставлена">
                      {fmtDate(к.row.created_date)}
                    </td>
                    <td className="nowrap" data-label="Завершена">
                      {fmtDate(к.row.closed_date)}
                    </td>
                    <td data-label="Проект">{fmtText(к.row.group_name)}</td>
                    <td data-label="Сделка">
                      {к.row.crm_deal_id
                        ? (к.row.deal_gone
                          ? `№${к.row.crm_deal_id} (в зеркале её нет)`
                          : fmtText(к.row.deal_title))
                        : '—'}
                    </td>
                    {/* Сами файлы не переносятся — только счётчик: таково правило про
                        фото финансовых документов и ссылки без авторизации. */}
                    <td className="num" data-label="Файлы">{к.row.files_count}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {(к.row.accomplices_info.length > 0 || к.row.auditors_info.length > 0) && (
              <div className="hint" style={{ marginTop: 8 }}>
                {к.row.accomplices_info.length > 0 && (
                  <>Соисполнители: {к.row.accomplices_info.map((ч) => ч.name).join(', ')}. </>
                )}
                {к.row.auditors_info.length > 0 && (
                  <>Наблюдатели: {к.row.auditors_info.map((ч) => ч.name).join(', ')}.</>
                )}
              </div>
            )}

            <Комментарии к={к} />
          </>
        )}
      </QueryState>
    </div>
  )
}

/**
 * Лента комментариев. Три разных «пусто», и слова у каждого свои: ядро отдаёт охват
 * второго прохода синка, а экран обязан его произнести. Пустая лента без этого
 * читалась бы как «никто не писал» — а комментарии, может быть, просто ещё едут.
 */
function Комментарии({ к }: { к: Карточка }) {
  const охват = к.comments_coverage
  return (
    <div style={{ marginTop: 16 }}>
      <b>Комментарии</b>

      {охват.state !== 'covered' && (
        <div className="notice" style={{ margin: '8px 0' }}>
          <b>Лента может быть неполной.</b>
          <div style={{ marginTop: 6 }}>
            {охват.why}
            {к.comments_declared > 0 && (
              <> Bitrix насчитывает у этой задачи {pluralN(к.comments_declared,
                'комментарий', 'комментария', 'комментариев')}.</>
            )}
          </div>
        </div>
      )}

      {к.comments.length === 0 ? (
        <div className="empty" style={{ marginTop: 8 }}>
          <div className="empty-title">
            {охват.state === 'covered'
              ? 'Комментариев нет'
              : 'Комментарии ещё не доехали'}
          </div>
          <div className="empty-hint">
            {охват.state === 'covered'
              ? 'И это проверено: второй проход синка эту задачу видел. Пустая лента здесь — ответ, а не пробел.'
              : охват.why}
          </div>
        </div>
      ) : (
        <div className="table-wrap cards" style={{ marginTop: 8 }}>
          <table>
            <thead>
              <tr>
                <th>Кто</th>
                <th className="nowrap">Когда</th>
                <th>Текст</th>
                <th>Вложения</th>
              </tr>
            </thead>
            <tbody>
              {к.comments.map((c) => (
                <tr key={c.bitrix_id}>
                  <td data-label="Кто">{fmtText(c.author)}</td>
                  <td className="nowrap" data-label="Когда">{fmtDate(c.post_date, true)}</td>
                  <td data-label="Текст" style={{ whiteSpace: 'pre-wrap' }}>
                    {c.message_redacted
                      ? `[текст вырезан синком: ${c.message_redacted}]`
                      : fmtText(c.message)}
                  </td>
                  {/* Имя и размер файла синк оставил, ссылку — нет: ссылки Bitrix несут
                      ключ доступа в адресе, и переносить их запрещено прямо. */}
                  <td data-label="Вложения">
                    {c.files_count > 0
                      ? (c.files ?? []).map((f) => f.name).filter(Boolean).join(', ') || pluralN(
                        c.files_count, 'файл', 'файла', 'файлов')
                      : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// ───────────────────────── вкладка «На ком висит» ─────────────────────────

function НаКомВисит() {
  const зпр = useСвод()
  const д = зпр.data
  return (
    <QueryState isLoading={зпр.isLoading} error={зпр.error}>
      <div className="card">
        <div className="table-wrap cards">
          <table>
            <thead>
              <tr>
                <th>Сотрудник</th>
                <th className="num">Задач</th>
                <th className="num">Ждёт выполнения</th>
                <th className="num">Выполняется</th>
                <th className="num">Ждёт контроля</th>
                <th className="num">Отложено</th>
                <th className="num">Срок прошёл</th>
                <th className="num">Срок сегодня</th>
                <th className="num">Без срока</th>
                <th className="num">Самая старая</th>
              </tr>
            </thead>
            <tbody>
              {(д?.rows ?? []).map((р) => (
                <tr key={String(р.responsible_id)}>
                  <td data-label="Сотрудник">
                    {fmtText(р.name)}
                    {неРаботает(р.active) && (
                      <span className="badge lose" style={{ marginLeft: 6 }}>не работает</span>
                    )}
                  </td>
                  <td className="num" data-label="Задач">{р.tasks}</td>
                  <td className="num" data-label="Ждёт выполнения">{р.pending}</td>
                  <td className="num" data-label="Выполняется">{р.in_progress}</td>
                  <td className="num" data-label="Ждёт контроля">{р.control}</td>
                  <td className="num" data-label="Отложено">{р.deferred}</td>
                  <td className="num" data-label="Срок прошёл">{р.overdue}</td>
                  <td className="num" data-label="Срок сегодня">{р.due_today}</td>
                  {/* Отдельным столбцом, а не в просрочке: это разные вещи. */}
                  <td className="num" data-label="Без срока">{р.no_due}</td>
                  <td className="num" data-label="Самая старая">
                    {р.oldest_days === null
                      ? '—' : pluralN(р.oldest_days, 'день', 'дня', 'дней')}
                  </td>
                </tr>
              ))}
              {д && д.rows.length === 0 && (
                <tr>
                  <td colSpan={10} className="empty">
                    <div className="empty-title">Открытых задач нет ни на ком</div>
                    <div className="empty-hint">
                      Либо всё завершено, либо синк зеркала ещё не привозил задач — свежесть
                      видна в оговорках наверху.
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {д && <div className="hint" style={{ marginTop: 8 }}>{д.sum_note}</div>}
      </div>
    </QueryState>
  )
}

// ───────────────────────── вкладка «Сводка» ─────────────────────────

function СводкаЗадач() {
  const зпр = useСводка()
  const д = зпр.data
  return (
    <QueryState isLoading={зпр.isLoading} error={зпр.error}>
      <div className="card">
        <b>По статусам</b>
        <div className="table-wrap cards" style={{ marginTop: 8 }}>
          <table>
            <thead>
              <tr>
                <th>Статус</th>
                <th className="num">Задач</th>
                <th className="num">Без срока</th>
                <th className="num">Срок прошёл</th>
              </tr>
            </thead>
            <tbody>
              {(д?.by_status ?? []).map((р) => (
                <tr key={String(р.status)}>
                  <td data-label="Статус">{р.status_name ?? fmtText(р.status)}</td>
                  <td className="num" data-label="Задач">{р.tasks ?? 0}</td>
                  <td className="num" data-label="Без срока">{р.no_due ?? 0}</td>
                  <td className="num" data-label="Срок прошёл">{р.overdue ?? 0}</td>
                </tr>
              ))}
              {д && д.by_status.length === 0 && (
                <tr>
                  <td colSpan={4} className="empty">
                    <div className="empty-title">Считать нечего</div>
                    <div className="empty-hint">
                      В зеркале нет ни одной задачи — это состояние синка, а не «работы нет».
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {д && д.by_group.length > 0 && (
        <div className="card" style={{ marginTop: 16 }}>
          <b>По проектам</b>
          <div className="hint" style={{ marginTop: 6 }}>{д.group_note}</div>
          <div className="table-wrap cards" style={{ marginTop: 8 }}>
            <table>
              <thead>
                <tr>
                  <th>Проект</th>
                  <th className="num">Всего</th>
                  <th className="num">Открыто</th>
                  <th className="num">Срок прошёл</th>
                </tr>
              </thead>
              <tbody>
                {д.by_group.map((п) => (
                  <tr key={String(п.group_id)}>
                    <td data-label="Проект">
                      {п.group_id === null ? 'без проекта' : fmtText(п.group_name)}
                    </td>
                    <td className="num" data-label="Всего">{п.tasks}</td>
                    <td className="num" data-label="Открыто">{п.open}</td>
                    <td className="num" data-label="Срок прошёл">{п.overdue}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {д && д.by_month.length > 0 && (
        <div className="card" style={{ marginTop: 16 }}>
          <b>По месяцам</b>
          <div className="table-wrap cards" style={{ marginTop: 8 }}>
            <table>
              <thead>
                <tr>
                  <th className="nowrap">Месяц</th>
                  <th className="num">Заведено</th>
                  <th className="num">Завершено</th>
                  <th className="num">Ещё открыто</th>
                </tr>
              </thead>
              <tbody>
                {д.by_month.map((м) => (
                  <tr key={м.month}>
                    <td className="nowrap" data-label="Месяц">{м.month}</td>
                    <td className="num" data-label="Заведено">{м.created}</td>
                    <td className="num" data-label="Завершено">{м.closed}</td>
                    <td className="num" data-label="Ещё открыто">{м.still_open}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </QueryState>
  )
}
